import React, { useState, useEffect, useMemo } from 'react';
import { api } from '../../api/client';
import { AuditLogEntry } from '../../types';
import { DataTable } from '../../components/common/DataTable';
import { ColumnDef } from '@tanstack/react-table';
import { History, Shield, User, Settings, RefreshCw } from 'lucide-react';

// Friendly labels for the raw action strings the two backend tables store.
// Anything not listed falls back to the raw value, so a new action type is
// never hidden — it just shows unformatted until it earns a label here.
const ACTION_LABELS: Record<string, string> = {
  'customer.update': 'Customer details edited',
  'customer.block': 'Customer blocked',
  'customer.unblock': 'Customer unblocked',
  'customer.reset_password': 'Password reset',
  'device.delete': 'Camera deleted',
  manual_activated: 'Manual payment / plan activated',
  plan_updated: 'Plan catalogue edited',
  subscribed: 'Subscribed (free)',
  subscribed_pending: 'Subscribe started (awaiting payment)',
  plan_change_staged: 'Plan change staged',
  mandate_setup: 'Auto-pay mandate set up',
  canceled: 'Subscription canceled',
  trial_started: 'Trial started',
  store_order_created: 'Store order created',
  'soxara_payment.completed': 'Payment completed',
  'soxara_payment.failed': 'Payment failed',
  'soxara_mandate.approved': 'Mandate approved',
  'soxara_mandate.revoked': 'Mandate revoked',
};

const SOURCE_STYLE: Record<AuditLogEntry['source'], { bg: string; border: string; color: string; label: string }> = {
  admin: { bg: 'rgba(56,189,248,0.10)', border: 'rgba(56,189,248,0.35)', color: 'var(--primary-light)', label: 'Operator' },
  customer: { bg: 'rgba(148,163,184,0.10)', border: 'rgba(148,163,184,0.30)', color: 'var(--text-muted)', label: 'Customer' },
  system: { bg: 'rgba(245,158,11,0.10)', border: 'rgba(245,158,11,0.30)', color: 'var(--warning-text, #f59e0b)', label: 'System' },
};

// Render a metadata object as a compact, readable summary rather than raw JSON.
function summarizeMetadata(m: Record<string, unknown> | null | undefined): string {
  if (!m || typeof m !== 'object') return '';
  // owner_id/owner_email already render in the affected-customer column.
  const skip = new Set(['owner_id', 'owner_email']);
  const parts: string[] = [];
  for (const [k, v] of Object.entries(m)) {
    if (skip.has(k)) continue;
    if (v === null || v === undefined || v === '') continue;
    if (typeof v === 'object') continue; // skip nested blobs in the one-line summary
    const key = k.replace(/_/g, ' ');
    parts.push(`${key}: ${String(v)}`);
  }
  return parts.join(' · ');
}

export const AuditLogPage: React.FC = () => {
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sourceFilter, setSourceFilter] = useState<'all' | 'admin' | 'customer' | 'system'>('all');

  useEffect(() => {
    loadLogs();
  }, []);

  const loadLogs = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getAuditLogs(300, 0);
      setLogs(data);
    } catch (err) {
      console.error('Failed to load audit logs', err);
      setError('Could not load the audit log. Check that you are signed in as an admin.');
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(
    () => (sourceFilter === 'all' ? logs : logs.filter((l) => l.source === sourceFilter)),
    [logs, sourceFilter],
  );

  const operatorCount = useMemo(() => logs.filter((l) => l.source === 'admin').length, [logs]);

  const columns = useMemo<ColumnDef<AuditLogEntry>[]>(
    () => [
      {
        accessorKey: 'createdAt',
        header: 'When',
        cell: (info) => (
          <span style={{ fontSize: '12px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
            {new Date(info.getValue() as string).toLocaleString()}
          </span>
        ),
      },
      {
        accessorKey: 'source',
        header: 'Who',
        cell: (info) => {
          const l = info.row.original;
          const s = SOURCE_STYLE[l.source] ?? SOURCE_STYLE.system;
          const actor = l.actorEmail || l.actorName;
          return (
            <div>
              <span
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '4px',
                  fontSize: '10.5px', fontWeight: 600, textTransform: 'uppercase',
                  letterSpacing: '0.03em', color: s.color, background: s.bg,
                  border: `1px solid ${s.border}`, padding: '1px 6px', borderRadius: '4px',
                }}
              >
                {l.source === 'admin' ? <User size={11} /> : l.source === 'system' ? <Settings size={11} /> : null}
                {s.label}
              </span>
              {actor && (
                <div style={{ fontSize: '11.5px', color: 'var(--text-main)', marginTop: '3px' }}>
                  {actor}
                </div>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: 'action',
        header: 'Action',
        cell: (info) => {
          const raw = info.getValue() as string;
          return (
            <div>
              <div style={{ fontSize: '12.5px', color: 'var(--text-main)', fontWeight: 500 }}>
                {ACTION_LABELS[raw] ?? raw}
              </div>
              <code style={{ fontSize: '10.5px', color: 'var(--text-dim)' }}>{raw}</code>
            </div>
          );
        },
      },
      {
        id: 'target',
        header: 'Affected customer',
        cell: (info) => {
          const l = info.row.original;
          const label = l.targetEmail || l.targetName;
          if (!label) return <span style={{ color: 'var(--text-dim)', fontSize: '11px' }}>—</span>;
          return (
            <div>
              <div style={{ fontSize: '12px', color: 'var(--text-main)' }}>{label}</div>
              {l.targetName && l.targetEmail && (
                <div style={{ fontSize: '11px', color: 'var(--text-dim)' }}>{l.targetName}</div>
              )}
            </div>
          );
        },
      },
      {
        id: 'details',
        header: 'Details',
        cell: (info) => {
          const summary = summarizeMetadata(info.row.original.metadata);
          if (!summary) return <span style={{ color: 'var(--text-dim)', fontSize: '11px' }}>—</span>;
          return (
            <span style={{ fontSize: '11.5px', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
              {summary}
            </span>
          );
        },
      },
    ],
    [],
  );

  const filterChip = (key: typeof sourceFilter, label: string) => {
    const active = sourceFilter === key;
    return (
      <button
        key={key}
        onClick={() => setSourceFilter(key)}
        style={{
          fontSize: '12px', padding: '5px 12px', borderRadius: '999px', cursor: 'pointer',
          border: `1px solid ${active ? 'var(--primary-light)' : 'var(--border-dim)'}`,
          background: active ? 'rgba(56,189,248,0.12)' : 'transparent',
          color: active ? 'var(--primary-light)' : 'var(--text-muted)', fontWeight: active ? 600 : 400,
        }}
      >
        {label}
      </button>
    );
  };

  return (
    <div>
      <div
        style={{
          background: 'var(--bg-surface-card)', border: '1px solid var(--border-dim)',
          borderRadius: 'var(--radius-lg)', padding: '16px 20px', marginBottom: '16px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap',
        }}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
            <History size={18} color="var(--primary-light)" />
            <h2 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text-main)' }}>
              Admin Audit Trail
            </h2>
          </div>
          <p style={{ fontSize: '12px', color: 'var(--text-muted)', maxWidth: '620px' }}>
            Operator actions on customer accounts (edit, block, unblock, password reset) alongside the
            subscription &amp; billing lifecycle. Recorded server-side; newest first.
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--success-text)' }}>
          <Shield size={16} />
          <span>{operatorCount} operator action{operatorCount === 1 ? '' : 's'} recorded</span>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px', flexWrap: 'wrap' }}>
        {filterChip('all', `All (${logs.length})`)}
        {filterChip('admin', 'Operator')}
        {filterChip('customer', 'Customer')}
        {filterChip('system', 'System')}
        <button
          onClick={loadLogs}
          disabled={loading}
          style={{
            marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: '6px',
            fontSize: '12px', padding: '5px 12px', borderRadius: '6px', cursor: loading ? 'default' : 'pointer',
            border: '1px solid var(--border-dim)', background: 'transparent', color: 'var(--text-muted)',
          }}
        >
          <RefreshCw size={13} className={loading ? 'spin' : ''} />
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {error && (
        <div
          style={{
            background: 'rgba(239,68,68,0.10)', border: '1px solid rgba(239,68,68,0.35)',
            color: 'var(--danger-text)', borderRadius: 'var(--radius-lg)', padding: '10px 16px',
            marginBottom: '16px', fontSize: '13px',
          }}
        >
          {error}
        </div>
      )}

      <DataTable
        storageKey="auditLog"
        data={filtered}
        columns={columns}
        searchPlaceholder="Filter by actor, action, customer, keyword…"
        exportFilename="mwm_admin_audit_log.csv"
      />
    </div>
  );
};
